import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Ticket as TicketIcon, AlertCircle, MessageCircle, Send, Paperclip, FileText, Image, Download, ChevronLeft, Clock, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminTickets() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-accent/10 text-accent border-accent/20';
      case 'in_progress': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'resolved': return 'bg-primary/10 text-primary border-primary/20';
      case 'closed': return 'bg-muted/20 text-muted-foreground border-muted/30';
      default: return 'bg-muted/20 text-muted-foreground border-muted/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-destructive';
      case 'medium': return 'text-accent';
      case 'low': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Tickets">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  if (selectedTicket) {
    return (
      <AdminLayout title="Ticket Management">
        <AdminTicketDetail 
          ticket={selectedTicket} 
          onBack={() => {
            setSelectedTicket(null);
            fetchTickets();
          }}
          onUpdate={fetchTickets}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tickets">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <TicketIcon className="h-8 w-8 text-secondary" />
          <h2 className="text-2xl font-mono font-bold">Ticket Management</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
            <div key={status} className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground font-mono capitalize">{status.replace('_', ' ')}</p>
              <p className="text-2xl font-mono font-bold">
                {tickets.filter(t => t.status === status).length}
              </p>
            </div>
          ))}
        </div>

        {tickets.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Tickets</h3>
            <p className="text-muted-foreground">Support tickets will appear here</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg divide-y divide-border">
            {tickets.map((ticket) => (
              <div 
                key={ticket.ticket_id} 
                className="p-4 hover:bg-accent/5 cursor-pointer transition-colors"
                onClick={() => setSelectedTicket(ticket)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-mono font-bold">{ticket.subject}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`text-xs font-mono font-bold ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                    <p className="text-xs text-muted-foreground font-mono mt-2">
                      ID: {ticket.ticket_id} • User: {ticket.user_id}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xs text-muted-foreground font-mono">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-muted-foreground">
                      <MessageCircle className="h-4 w-4" />
                      <span className="text-xs">View</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminTicketDetail({ ticket, onBack, onUpdate }) {
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(ticket.status);
  const [updating, setUpdating] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    fetchAttachments();
  }, [ticket.ticket_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/messages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/attachments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Failed to load attachments');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage })
      });
      if (response.ok) {
        setNewMessage('');
        fetchMessages();
        toast.success('Reply sent');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusUpdate = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success('Status updated');
        onUpdate();
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type,
            data: base64Data
          })
        });

        if (response.ok) {
          toast.success('File uploaded');
          fetchAttachments();
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/tickets/${ticket.ticket_id}/attachments/${attachment.attachment_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const link = document.createElement('a');
        link.href = `data:${data.content_type};base64,${data.data}`;
        link.download = data.filename;
        link.click();
      }
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (contentType) => contentType?.startsWith('image/');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-mono font-bold">{ticket.subject}</h2>
          <p className="text-sm text-muted-foreground">
            ID: {ticket.ticket_id} • User: {ticket.user_id} • Created: {new Date(ticket.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Status Update */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-sm font-mono font-bold mb-2 block">Ticket Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="font-mono w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleStatusUpdate} 
            disabled={updating || status === ticket.status}
            className="bg-secondary text-secondary-foreground mt-6"
          >
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Messages Section */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg flex flex-col h-[500px]">
          <div className="p-4 border-b border-border">
            <h3 className="font-mono font-bold flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Conversation
            </h3>
          </div>
          
          {/* Messages List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Initial ticket */}
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary">C</span>
              </div>
              <div className="flex-1">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                  <p className="text-xs font-mono text-muted-foreground mb-1">Customer (Original Request)</p>
                  <p className="text-sm">{ticket.description}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(ticket.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Messages */}
            {messages.map((msg) => (
              <div key={msg.message_id} className="flex gap-3">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.user_role === 'admin' ? 'bg-secondary/20' : 'bg-primary/20'
                }`}>
                  <span className={`text-xs font-bold ${
                    msg.user_role === 'admin' ? 'text-secondary' : 'text-primary'
                  }`}>
                    {msg.user_role === 'admin' ? 'A' : 'C'}
                  </span>
                </div>
                <div className="flex-1">
                  <div className={`rounded-lg p-3 ${
                    msg.user_role === 'admin' 
                      ? 'bg-secondary/5 border border-secondary/20' 
                      : 'bg-primary/5 border border-primary/20'
                  }`}>
                    <p className="text-xs font-mono text-muted-foreground mb-1">
                      {msg.user_name} ({msg.user_role})
                    </p>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Input */}
          <div className="p-4 border-t border-border">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply to the customer..."
                className="flex-1 font-mono"
              />
              <Button 
                type="submit" 
                disabled={sending || !newMessage.trim()}
                className="bg-secondary text-secondary-foreground"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Attachments Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h3 className="font-mono font-bold flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments
            </h3>
          </div>
          
          <div className="p-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt,.csv"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="w-full mb-4"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : (
                <>
                  <Paperclip className="h-4 w-4 mr-2" />
                  Upload File
                </>
              )}
            </Button>

            {attachments.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No attachments</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((att) => (
                  <div
                    key={att.attachment_id}
                    className="flex items-center gap-2 p-2 bg-background border border-border rounded-md hover:border-secondary/50 cursor-pointer"
                    onClick={() => downloadAttachment(att)}
                  >
                    {isImage(att.content_type) ? (
                      <Image className="h-6 w-6 text-accent flex-shrink-0" />
                    ) : (
                      <FileText className="h-6 w-6 text-primary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono truncate">{att.filename}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(att.size)}</p>
                    </div>
                    <Download className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminTickets;
